import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import { isValidPhoneNumber, normalizePhoneNumber, sanitizePhoneDraft } from '../../lib/phone';
import { translateApiError } from '../../lib/translateApiError';
import AnimatedEyebrow from '../AnimatedEyebrow';
import CustomSelect from '../CustomSelect';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MAX_LENGTH = 80;
const EMAIL_MAX_LENGTH = 254;
const SUBJECT_MAX_LENGTH = 120;
const PHONE_MAX_LENGTH = 24;
const MESSAGE_MAX_LENGTH = 1000;

function trimSingleLine(value = '') {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    return { line1: words[0] || '', line2: '' };
  }

  return {
    line1: words.slice(0, -1).join(' '),
    line2: words.slice(-1).join(' '),
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

function getInitialForm() {
  return {
    fullName: '',
    email: '',
    subject: '',
    phone: '',
    message: '',
    inquiryType: '',
  };
}

function validateForm(values, t) {
  const errors = {};

  if (!trimSingleLine(values.fullName)) {
    errors.fullName = t('contactPage.form.errors.nameRequired');
  }

  const email = trimSingleLine(values.email).toLowerCase();
  if (!email) {
    errors.email = t('contactPage.form.errors.emailRequired');
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = t('contactPage.form.errors.invalidEmail');
  }

  if (!trimSingleLine(values.subject)) {
    errors.subject = t('contactPage.form.errors.subjectRequired');
  }

  const phone = String(values.phone || '').trim();
  if (!phone) {
    errors.phone = t('contactPage.form.errors.phoneRequired');
  } else if (!isValidPhoneNumber(phone)) {
    errors.phone = t('contactPage.form.errors.invalidPhone');
  }

  if (!values.inquiryType.trim()) {
    errors.inquiryType = t('contactPage.form.errors.inquiryTypeRequired');
  }

  if (!values.message.trim()) {
    errors.message = t('contactPage.form.errors.messageRequired');
  }

  return errors;
}

export default function ContactFormSection() {
  const { t } = useI18n();
  const [form, setForm] = useState(getInitialForm);
  const [touched, setTouched] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  const inquiryOptions = [
    { value: 'products', label: t('contactPage.form.options.products') },
    { value: 'spareParts', label: t('contactPage.form.options.spareParts') },
    { value: 'afterSales', label: t('contactPage.form.options.afterSales') },
    { value: 'other', label: t('contactPage.form.options.other') },
  ];
  const titleParts = useMemo(() => splitTitleParts(t('contactPage.form.title')), [t]);

  const resetForm = () => {
    setForm(getInitialForm());
    setTouched({});
    setHasSubmitted(false);
  };

  const validationErrors = validateForm(form, t);
  const isFieldInvalid = (field) => (touched[field] || hasSubmitted) && Boolean(validationErrors[field]);
  const markFieldTouched = (field) => {
    setTouched((current) => ({
      ...current,
      [field]: true,
    }));
  };

  useEffect(() => {
    if (!feedback.text) {
      setFeedbackVisible(false);
      return undefined;
    }

    setFeedbackVisible(true);
    const hideTimer = window.setTimeout(() => {
      setFeedbackVisible(false);
    }, 20000);
    const clearTimer = window.setTimeout(() => {
      setFeedback({ type: '', text: '' });
    }, 20500);

    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(clearTimer);
    };
  }, [feedback.text]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setHasSubmitted(true);
    setTouched({
      fullName: true,
      email: true,
      subject: true,
      phone: true,
      inquiryType: true,
      message: true,
    });

    const currentErrors = validateForm(form, t);
    if (Object.keys(currentErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: '', text: '' });

    const normalizedPayload = {
      fullName: trimSingleLine(form.fullName).slice(0, NAME_MAX_LENGTH),
      email: trimSingleLine(form.email).toLowerCase().slice(0, EMAIL_MAX_LENGTH),
      subject: trimSingleLine(form.subject).slice(0, SUBJECT_MAX_LENGTH),
      phone: normalizePhoneNumber(form.phone).slice(0, PHONE_MAX_LENGTH),
      inquiryType: trimSingleLine(form.inquiryType),
      message: String(form.message || '').trim().slice(0, MESSAGE_MAX_LENGTH),
    };

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizedPayload),
      });

      const rawBody = await response.text();
      let data = {};
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch (parseError) {
          if (response.ok) {
            setFeedback({ type: 'success', text: t('contactPage.form.success') });
            resetForm();
            return;
          }
        }
      }

      if (!response.ok) {
        throw new Error(
          translateApiError(t, data?.error || rawBody, t('contactPage.form.errors.failed'), {
            'Too many contact requests. Please try again later.': 'contactPage.form.errors.tooManyRequests',
            'Failed to send contact message': 'contactPage.form.errors.failed',
            'Please enter a valid phone number.': 'contactPage.form.errors.invalidPhone',
          }),
        );
      }

      setFeedback({ type: 'success', text: t('contactPage.form.success') });
      resetForm();
    } catch (error) {
      setFeedback({
        type: 'error',
        text: translateApiError(t, error, t('contactPage.form.errors.failed'), {
          'Too many contact requests. Please try again later.': 'contactPage.form.errors.tooManyRequests',
          'Failed to send contact message': 'contactPage.form.errors.failed',
          'Please enter a valid phone number.': 'contactPage.form.errors.invalidPhone',
        }),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact-form"
      className="relative overflow-hidden bg-[var(--dash-other)] py-20 text-[var(--dash-black)] sm:py-24"
    >
      <div className="absolute inset-0 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%] opacity-20" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.45)_0%,transparent_36%),radial-gradient(circle_at_85%_10%,rgba(255,93,16,0.14)_0%,transparent_28%)]" />

      <div className="container mx-auto px-6 lg:px-10 relative z-10">
        <motion.div
          className="mb-8 mx-auto max-w-3xl text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <div className="flex justify-center">
            <AnimatedEyebrow>{t('contactPage.form.eyebrow')}</AnimatedEyebrow>
          </div>
          <motion.h2 variants={itemVariants} className="mt-4 text-3xl uppercase leading-none tracking-tight text-[var(--dash-black)] sm:text-4xl lg:text-5xl">
            <span className="font-light">{titleParts.line1}</span>{' '}
            <span className="font-black">{titleParts.line2}</span>
          </motion.h2>
          <motion.p variants={itemVariants} className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[var(--dash-black)] sm:text-base">
            {t('contactPage.form.description')}
          </motion.p>
        </motion.div>

        <div className="rounded-[32px] border border-white/10 bg-[var(--dash-black)] px-5 py-12 text-white shadow-[0_30px_80px_rgba(2,6,23,0.28)] backdrop-blur-md sm:px-7 lg:px-10">
        <motion.div
          className="grid grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <div className="lg:col-span-7 self-start">
            <AnimatePresence initial={false}>
              {feedback.text ? (
                <motion.div
                  key={`${feedback.type}-${feedback.text}`}
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: feedbackVisible ? 1 : 0, y: feedbackVisible ? 0 : -8, height: 'auto' }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className={`mb-6 overflow-hidden rounded-2xl border p-4 ${
                    feedback.type === 'success'
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-50'
                      : 'border-red-500/30 bg-red-500/10 text-red-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
                        feedback.type === 'success'
                          ? 'border-emerald-400/30 bg-emerald-400/15 text-emerald-200'
                          : 'border-red-400/30 bg-red-400/15 text-red-200'
                      }`}
                    >
                      {feedback.type === 'success' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <AlertCircle className="h-5 w-5" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold uppercase tracking-[0.14em]">
                        {feedback.type === 'success'
                          ? t('contactPage.form.successTitle')
                          : t('contactPage.form.errors.failed')}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed opacity-90">
                        {feedback.type === 'success'
                          ? t('contactPage.form.successDetail')
                          : feedback.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <motion.form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 gap-5 md:grid-cols-2" variants={containerVariants}>
            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-white">
                  {t('contactPage.form.fullName')}
                  <span className="text-[var(--dash-other)]">*</span>
                </span>
                <span className="text-xs font-normal normal-case tracking-normal text-slate-400">
                  {form.fullName.length}/{NAME_MAX_LENGTH}
                </span>
              </div>
              <input
                value={form.fullName}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    fullName: event.target.value.slice(0, NAME_MAX_LENGTH),
                  }))
                }
                onBlur={() => {
                  markFieldTouched('fullName');
                  setForm((current) => ({
                    ...current,
                    fullName: trimSingleLine(current.fullName).slice(0, NAME_MAX_LENGTH),
                  }));
                }}
                placeholder={t('contactPage.form.fullNamePlaceholder')}
                autoComplete="name"
                maxLength={NAME_MAX_LENGTH}
                autoCapitalize="words"
                spellCheck="false"
                required
                aria-invalid={isFieldInvalid('fullName')}
                aria-describedby={isFieldInvalid('fullName') ? 'contact-fullName-error' : undefined}
                className={`h-12 rounded-xl border px-4 text-sm text-white placeholder:text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
                  isFieldInvalid('fullName')
                    ? 'border-red-400/70 bg-red-500/12 focus:border-red-400/80'
                    : 'border-white/18 bg-white/8 focus:border-[var(--dash-main)] focus:bg-white/12'
                }`}
              />
              {isFieldInvalid('fullName') ? (
                <span id="contact-fullName-error" className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.fullName}
                </span>
              ) : null}
            </motion.label>

            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-white">
                  {t('contactPage.form.email')}
                  <span className="text-[var(--dash-other)]">*</span>
                </span>
                <span className="text-xs font-normal normal-case tracking-normal text-slate-400">
                  {form.email.length}/{EMAIL_MAX_LENGTH}
                </span>
              </div>
              <input
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value.slice(0, EMAIL_MAX_LENGTH),
                  }))
                }
                onBlur={() => {
                  markFieldTouched('email');
                  setForm((current) => ({
                    ...current,
                    email: trimSingleLine(current.email).slice(0, EMAIL_MAX_LENGTH),
                  }));
                }}
                placeholder={t('contactPage.form.emailPlaceholder')}
                autoComplete="email"
                maxLength={EMAIL_MAX_LENGTH}
                autoCapitalize="none"
                spellCheck={false}
                required
                aria-invalid={isFieldInvalid('email')}
                aria-describedby={isFieldInvalid('email') ? 'contact-email-error' : undefined}
                className={`h-12 rounded-xl border px-4 text-sm text-white placeholder:text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
                  isFieldInvalid('email')
                    ? 'border-red-400/70 bg-red-500/12 focus:border-red-400/80'
                    : 'border-white/18 bg-white/8 focus:border-[var(--dash-main)] focus:bg-white/12'
                }`}
              />
              {isFieldInvalid('email') ? (
                <span id="contact-email-error" className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.email}
                </span>
              ) : null}
            </motion.label>

            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200 md:col-span-2">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-white">
                  {t('contactPage.form.subject')}
                  <span className="text-[var(--dash-other)]">*</span>
                </span>
                <span className="text-xs font-normal normal-case tracking-normal text-slate-400">
                  {form.subject.length}/{SUBJECT_MAX_LENGTH}
                </span>
              </div>
              <input
                value={form.subject}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    subject: event.target.value.slice(0, SUBJECT_MAX_LENGTH),
                  }))
                }
                onBlur={() => {
                  markFieldTouched('subject');
                  setForm((current) => ({
                    ...current,
                    subject: trimSingleLine(current.subject).slice(0, SUBJECT_MAX_LENGTH),
                  }));
                }}
                placeholder={t('contactPage.form.subjectPlaceholder')}
                autoComplete="off"
                maxLength={SUBJECT_MAX_LENGTH}
                autoCapitalize="sentences"
                spellCheck={true}
                required
                aria-invalid={isFieldInvalid('subject')}
                aria-describedby={isFieldInvalid('subject') ? 'contact-subject-error' : undefined}
                className={`h-12 rounded-xl border px-4 text-sm text-white placeholder:text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
                  isFieldInvalid('subject')
                    ? 'border-red-400/70 bg-red-500/12 focus:border-red-400/80'
                    : 'border-white/18 bg-white/8 focus:border-[var(--dash-main)] focus:bg-white/12'
                }`}
              />
              {isFieldInvalid('subject') ? (
                <span id="contact-subject-error" className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.subject}
                </span>
              ) : null}
            </motion.label>

            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1">
                  {t('contactPage.form.phone')}
                  <span className="text-[var(--dash-other)]">*</span>
                </span>
                <span className="text-xs font-normal normal-case tracking-normal text-slate-400">
                  {form.phone.length}/{PHONE_MAX_LENGTH}
                </span>
              </div>
              <input
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone: sanitizePhoneDraft(event.target.value).slice(0, PHONE_MAX_LENGTH),
                  }))
                }
                onBlur={() => {
                  markFieldTouched('phone');
                  setForm((current) => ({
                    ...current,
                    phone: normalizePhoneNumber(current.phone).slice(0, PHONE_MAX_LENGTH),
                  }));
                }}
                placeholder={t('contactPage.form.phonePlaceholder')}
                autoComplete="tel"
                inputMode="tel"
                pattern="[+0-9()\\s-]*"
                maxLength={PHONE_MAX_LENGTH}
                autoCapitalize="none"
                spellCheck={false}
                required
                aria-invalid={isFieldInvalid('phone')}
                aria-describedby={isFieldInvalid('phone') ? 'contact-phone-error' : undefined}
                className={`h-12 rounded-xl border px-4 text-sm text-white placeholder:text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
                  isFieldInvalid('phone')
                    ? 'border-red-400/70 bg-red-500/12 focus:border-red-400/80'
                    : 'border-white/18 bg-white/8 focus:border-[var(--dash-main)] focus:bg-white/12'
                }`}
              />
              {isFieldInvalid('phone') ? (
                <span id="contact-phone-error" className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.phone}
                </span>
              ) : null}
            </motion.label>

            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
              <span className="inline-flex items-center gap-1">
                {t('contactPage.form.inquiryType')}
                <span className="text-[var(--dash-other)]">*</span>
              </span>
              <CustomSelect
                value={form.inquiryType}
                onChange={(value) => {
                  setForm((current) => ({
                    ...current,
                    inquiryType: value,
                  }));
                  markFieldTouched('inquiryType');
                }}
                options={inquiryOptions}
                placeholder={t('contactPage.form.inquiryTypePlaceholder')}
                searchable={false}
                className="w-full"
                triggerClassName={`!h-12 !rounded-xl !px-4 !text-sm !text-white ${
                  isFieldInvalid('inquiryType')
                    ? '!border-red-400/60 !bg-red-500/10'
                    : '!border-white/15 !bg-white/10'
                }`}
                dropdownClassName="!border-white/10 !bg-[var(--dash-black)] !text-white"
              />
              {isFieldInvalid('inquiryType') ? (
                <span className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.inquiryType}
                </span>
              ) : null}
            </motion.label>

            <motion.label variants={itemVariants} className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-300 md:col-span-2">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1">
                  {t('contactPage.form.message')}
                  <span className="text-[var(--dash-other)]">*</span>
                </span>
                <span className="text-xs font-normal normal-case tracking-normal text-slate-400">
                  {form.message.length}/{MESSAGE_MAX_LENGTH}
                </span>
              </div>
              <textarea
                value={form.message}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    message: event.target.value.slice(0, MESSAGE_MAX_LENGTH),
                  }))
                }
                onBlur={() => {
                  markFieldTouched('message');
                  setForm((current) => ({
                    ...current,
                    message: String(current.message || '').trim().slice(0, MESSAGE_MAX_LENGTH),
                  }));
                }}
                placeholder={t('contactPage.form.messagePlaceholder')}
                maxLength={MESSAGE_MAX_LENGTH}
                autoCapitalize="sentences"
                spellCheck={true}
                required
                aria-invalid={isFieldInvalid('message')}
                aria-describedby={isFieldInvalid('message') ? 'contact-message-error' : undefined}
                className={`min-h-[180px] resize-y rounded-xl border px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] ${
                  isFieldInvalid('message')
                    ? 'border-red-400/70 bg-red-500/12 focus:border-red-400/80'
                    : 'border-white/18 bg-white/8 focus:border-[var(--dash-main)] focus:bg-white/12'
                }`}
              />
              {isFieldInvalid('message') ? (
                <span id="contact-message-error" className="text-xs font-normal normal-case tracking-normal text-red-300">
                  {validationErrors.message}
                </span>
              ) : null}
            </motion.label>

            <motion.div variants={itemVariants} className="flex flex-col gap-4 pt-1 md:col-span-2 md:flex-row md:items-center md:justify-between">
              <div aria-live="polite" className="space-y-1 text-sm leading-relaxed text-slate-300">
                <p>{feedback.text || t('contactPage.form.note')}</p>
                <p className="text-xs text-slate-400">{t('contactPage.form.requiredNote')}</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--dash-main)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-white hover:text-[var(--dash-black)] focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)] disabled:cursor-wait disabled:opacity-60"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? t('contactPage.form.sending') : t('contactPage.form.sendMessage')}
              </button>
            </motion.div>
          </motion.form>
          </div>

          <div className="hidden lg:block lg:col-span-5 self-start">
            <motion.div
              variants={itemVariants}
              className="relative h-[560px] w-full overflow-hidden rounded-3xl lg:h-[560px] xl:h-[620px]"
            >
              <ImageOnlySkeletonLoader
                src="/contact/1.webp"
                mode="native"
                alt="Contact us"
                containerClassName="h-full w-full"
                imgClassName="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--dash-black)]/60 via-transparent to-transparent" />
            </motion.div>
          </div>
        </motion.div>
        </div>
      </div>
    </section>
  );
}
